import moment from 'moment';

import { API_DATE_FORMAT } from '../../../../../constants';
import {
  ALWAYS_MANDATORY_ASSESSMENT_TOOLS,
  ASSESSMENT_READINESS_ITEM_MAX_SCORE,
  ASSESSMENT_READINESS_OVERALL_MAX_SCORE,
  ASSESSMENT_READINESS_SECTIONS,
  ASSESSMENT_TOOL_DESCRIPTIONS,
  ASSESSMENT_TOOL_IDS,
  MIN_CLIENT_AGE_FOR_AFLS,
} from './constants';
import {
  ASSESSMENT_BEHAVIOR_FORM_SCHEMA,
  ASSESSMENT_INFO_FORM_SCHEMA,
  ASSESSMENT_METHOD_FORM_SCHEMA,
  ASSESSMENT_METHOD_VISUAL_FORM_SCHEMA,
  ASSESSMENT_OBSERVATIONS_FORM_SCHEMA,
  ASSESSMENT_READINESS_DATE_FORM_SCHEMA,
  ASSESSMENT_READINESS_NOTES_FORM_SCHEMA,
  ASSESSMENT_SUMMARY_FORM_SCHEMA,
} from './validationSchema';

import type {
  AssessmentReadinessValue,
  ITreatmentPlanV3AssessmentBehavior,
  ITreatmentPlanV3AssessmentMethod,
  ITreatmentPlanV3AssessmentMethodVisual,
  ITreatmentPlanV3AssessmentPage,
  ITreatmentPlanV3AssessmentTemplates,
  TreatmentPlanV3AssessmentBehaviorPayload,
  TreatmentPlanV3AssessmentMethodPayload,
  TreatmentPlanV3AssessmentMethodVisualPayload,
  TreatmentPlanV3AssessmentPagePayload,
} from '../../../../../store/treatmentPlanV3';
import type { AutoSaveListDeleteConfirm, ITreatmentPlanV3CrisisPlan } from '../../../../common';

import type {
  AssessmentBehaviorFieldName,
  AssessmentBehaviorFormValues,
  AssessmentInfoFieldName,
  AssessmentInfoFormValues,
  AssessmentMethodFieldName,
  AssessmentMethodFormValues,
  AssessmentMethodVisualFieldName,
  AssessmentMethodVisualFormValues,
  AssessmentObservationsFieldName,
  AssessmentObservationsFormValues,
  AssessmentReadinessDateFormValues,
  AssessmentSummaryFieldName,
  AssessmentSummaryFormValues,
} from './types';

export function toAssessmentBehaviorFieldPayload(
  name: AssessmentBehaviorFieldName,
  values: AssessmentBehaviorFormValues,
): TreatmentPlanV3AssessmentBehaviorPayload {
  // Send ONLY the changed field: bundling others lets an older in-flight save clobber newer edits.
  const data: TreatmentPlanV3AssessmentBehaviorPayload = {};
  data[name] = values[name];
  return data;
}

export function mapAssessmentBehaviorRowToValues(
  row: ITreatmentPlanV3AssessmentBehavior,
): AssessmentBehaviorFormValues {
  return {
    behavior: row.behavior,
    dsm5_symptom: row.dsm5_symptom,
    parent_concerns: row.parent_concerns,
  };
}

export function getAssessmentBehaviorDeleteMessage(row: ITreatmentPlanV3AssessmentBehavior) {
  if (!row.behavior) {
    return 'Are you sure you want to delete this maladaptive behavior?';
  }
  return `Are you sure you want to delete maladaptive behavior "${row.behavior}"?`;
}

export function getAssessmentBehaviorDeleteBlock(
  row: ITreatmentPlanV3AssessmentBehavior,
  crisisPlans: ReadonlyArray<ITreatmentPlanV3CrisisPlan>,
): AutoSaveListDeleteConfirm | null {
  if (!crisisPlans.some((plan) => plan.assessment_behavior_id === row.id)) {
    return null;
  }
  return {
    title: 'Cannot delete maladaptive behavior',
    message: 'Delete the crisis plan first before deleting this maladaptive behavior.',
    confirmLabel: 'OK',
  };
}

export function mapAssessmentPageToInfoValues(
  page: ITreatmentPlanV3AssessmentPage,
): AssessmentInfoFormValues {
  return {
    initial_assessment_date: page.initial_assessment_date
      ? moment(page.initial_assessment_date)
      : null,
    diagnostic_report_reviewed: page.diagnostic_report_reviewed ? 'Yes' : 'No',
    reassessment_date: page.reassessment_date ? moment(page.reassessment_date) : null,
    parent_interview_summary: page.parent_interview_summary ?? '',
    additional_files_viewed: [
      ...new Set(
        (page.additional_files_viewed ?? []).map((value) =>
          value === 'Other (if needed)' ? 'Other' : value,
        ),
      ),
    ],
  };
}

export function toAssessmentInfoFieldPayload(
  name: AssessmentInfoFieldName,
  values: AssessmentInfoFormValues,
): TreatmentPlanV3AssessmentPagePayload {
  switch (name) {
    case 'initial_assessment_date':
      return {
        initial_assessment_date: values.initial_assessment_date?.format(API_DATE_FORMAT) ?? null,
      };
    case 'reassessment_date':
      return {
        reassessment_date: values.reassessment_date?.format(API_DATE_FORMAT) ?? null,
      };
    case 'diagnostic_report_reviewed':
      return { diagnostic_report_reviewed: values.diagnostic_report_reviewed === 'Yes' };
    case 'parent_interview_summary':
      return { parent_interview_summary: values.parent_interview_summary || null };
    case 'additional_files_viewed':
      return { additional_files_viewed: values.additional_files_viewed };
  }
}

export function toAssessmentMethodFieldPayload(
  name: AssessmentMethodFieldName,
  values: AssessmentMethodFormValues,
): TreatmentPlanV3AssessmentMethodPayload {
  switch (name) {
    case 'tool':
      return { tool: values.tool };
    case 'assessment_date':
      return { assessment_date: values.assessment_date?.format(API_DATE_FORMAT) ?? null };
    case 'rationale':
      return { rationale: values.rationale || null };
    case 'selection_rationale':
      return { selection_rationale: values.selection_rationale || null };
    case 'tool_description':
      return { tool_description: values.tool_description || null };
    case 'client_score':
      return { client_score: values.client_score || null };
  }
}

export function mapAssessmentMethodRowToValues(
  row: ITreatmentPlanV3AssessmentMethod,
): AssessmentMethodFormValues {
  return {
    tool: row.tool ?? '',
    assessment_date: row.assessment_date ? moment(row.assessment_date) : null,
    rationale: row.rationale ?? '',
    selection_rationale: row.selection_rationale ?? '',
    tool_description: row.tool_description ?? '',
    client_score: row.client_score ?? '',
  };
}

export function getAssessmentToolDescription(tool: string) {
  for (const [id, description] of Object.entries(ASSESSMENT_TOOL_DESCRIPTIONS)) {
    if (id === tool) return description;
  }
  return '';
}

export function getAssessmentMethodDeleteMessage(row: ITreatmentPlanV3AssessmentMethod) {
  if (!row.tool) {
    return 'Are you sure you want to delete this assessment?';
  }
  return `Are you sure you want to delete assessment "${row.tool}"?`;
}

export function getAssessmentMethodTemplateValues(
  tool: string,
  templates: ITreatmentPlanV3AssessmentTemplates | null,
) {
  const methodTemplates = templates?.methods[tool];
  return {
    rationale: methodTemplates?.rationale ?? '',
    selection_rationale: methodTemplates?.selection_rationale ?? '',
    tool_description: methodTemplates?.tool_description || getAssessmentToolDescription(tool),
    client_score: methodTemplates?.client_score ?? '',
  };
}

export function getClientAgeAtAssessment(
  dob: Date | string | undefined,
  assessmentDate: string | null,
) {
  if (!dob) return null;
  const age = (assessmentDate ? moment(assessmentDate) : moment()).diff(moment(dob), 'years');
  if (!Number.isFinite(age)) return null;
  return age;
}

export function getMissingRecommendedAssessmentToolsText(
  methods: ReadonlyArray<ITreatmentPlanV3AssessmentMethod>,
  clientAge: number | null,
) {
  const toolNames = methods.map((method) => method.tool);
  const missingTools: string[] = [];

  if (clientAge != null) {
    if (clientAge < MIN_CLIENT_AGE_FOR_AFLS) {
      if (!toolNames.includes(ASSESSMENT_TOOL_IDS.VB_MAPP)) {
        missingTools.push(ASSESSMENT_TOOL_IDS.VB_MAPP);
      }
    } else if (!toolNames.includes(ASSESSMENT_TOOL_IDS.AFLS)) {
      missingTools.push(ASSESSMENT_TOOL_IDS.AFLS);
    }
  }

  for (const tool of ALWAYS_MANDATORY_ASSESSMENT_TOOLS) {
    if (!toolNames.includes(tool)) {
      missingTools.push(tool);
    }
  }

  if (missingTools.length === 0) return null;
  return missingTools.join(', ');
}

export function toAssessmentMethodVisualFieldPayload(
  name: AssessmentMethodVisualFieldName,
  values: AssessmentMethodVisualFormValues,
): TreatmentPlanV3AssessmentMethodVisualPayload {
  return { [name]: values[name] };
}

export function mapAssessmentMethodVisualToValues(
  visual: ITreatmentPlanV3AssessmentMethodVisual,
): AssessmentMethodVisualFormValues {
  return {
    title: visual.title ?? '',
    analysis: visual.analysis ?? '',
  };
}

export function isAssessmentMethodVisualPreviewable(url: string) {
  if (!url) return false;
  return !url.split('?')[0].toLowerCase().endsWith('.pdf');
}

export function getAssessmentMethodVisualPreviewUrl(url: string, cacheKey = 0) {
  if (!url || !cacheKey) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${cacheKey}`;
}

export function isAssessmentMethodReady(row: ITreatmentPlanV3AssessmentMethod | null) {
  return (
    row?.visuals.some((visual) =>
      ASSESSMENT_METHOD_VISUAL_FORM_SCHEMA.isValidSync({
        title: visual.title,
        analysis: visual.analysis,
      }),
    ) ?? false
  );
}

export function mapAssessmentPageToReadinessDateValues(
  page: ITreatmentPlanV3AssessmentPage,
): AssessmentReadinessDateFormValues {
  return {
    telehealth_assessment_date: page.telehealth_assessment_date
      ? moment(page.telehealth_assessment_date)
      : null,
  };
}

export function toAssessmentReadinessDatePayload(
  _name: keyof AssessmentReadinessDateFormValues,
  values: AssessmentReadinessDateFormValues,
): TreatmentPlanV3AssessmentPagePayload {
  return {
    telehealth_assessment_date: values.telehealth_assessment_date?.format(API_DATE_FORMAT) ?? null,
  };
}

export function formatAssessmentReadinessSectionScore(score: number | null, itemCount: number) {
  return `${score ?? 0} out of ${itemCount * ASSESSMENT_READINESS_ITEM_MAX_SCORE}`;
}

export function formatAssessmentReadinessOverallScore(score: number | null) {
  return `${Math.round(((score ?? 0) / ASSESSMENT_READINESS_OVERALL_MAX_SCORE) * 100)}%`;
}

export function mapAssessmentPageToSummaryValues(
  page: ITreatmentPlanV3AssessmentPage,
): AssessmentSummaryFormValues {
  return {
    assessment_results: page.assessment_results ?? '',
    priorities: page.priorities ?? '',
  };
}

export function toAssessmentObservationsFieldPayload(
  _name: AssessmentObservationsFieldName,
  values: AssessmentObservationsFormValues,
): TreatmentPlanV3AssessmentPagePayload {
  return { observations: values.observations || null };
}

export function toAssessmentSummaryFieldPayload(
  name: AssessmentSummaryFieldName,
  values: AssessmentSummaryFormValues,
): TreatmentPlanV3AssessmentPagePayload {
  return { [name]: values[name] || null };
}

export function getCombinedAssessmentDsm5Symptoms(
  behaviors: ReadonlyArray<ITreatmentPlanV3AssessmentBehavior>,
  noMaladaptiveBehavior: boolean,
) {
  if (noMaladaptiveBehavior) return '';
  return [...new Set(behaviors.map((row) => row.dsm5_symptom).filter(Boolean))].join(', ');
}

export function isAssessmentReadinessItemValid(
  readiness: AssessmentReadinessValue | null | undefined,
  notes: string,
) {
  return readiness != null && ASSESSMENT_READINESS_NOTES_FORM_SCHEMA.isValidSync({ notes });
}

export function hasAssessmentReadinessErrors(
  page: ITreatmentPlanV3AssessmentPage,
  dateValues: AssessmentReadinessDateFormValues = mapAssessmentPageToReadinessDateValues(page),
) {
  if (!ASSESSMENT_READINESS_DATE_FORM_SCHEMA.isValidSync(dateValues)) {
    return true;
  }

  return ASSESSMENT_READINESS_SECTIONS.some((section) =>
    section.items.some((itemId) => {
      const savedItem = (page.readiness_items ?? []).find((row) => row.item === itemId);
      return !isAssessmentReadinessItemValid(savedItem?.readiness, savedItem?.notes ?? '');
    }),
  );
}

export function hasAssessmentFormErrors(page: ITreatmentPlanV3AssessmentPage) {
  if (!ASSESSMENT_INFO_FORM_SCHEMA.isValidSync(mapAssessmentPageToInfoValues(page))) {
    return true;
  }

  if (!page.no_maladaptive_behavior) {
    if (page.behaviors.length === 0) return true;
    if (
      page.behaviors.some(
        (row) => !ASSESSMENT_BEHAVIOR_FORM_SCHEMA.isValidSync(mapAssessmentBehaviorRowToValues(row)),
      )
    ) {
      return true;
    }
  }

  if (page.methods.length === 0) return true;
  if (
    page.methods.some(
      (method) =>
        !ASSESSMENT_METHOD_FORM_SCHEMA.isValidSync(mapAssessmentMethodRowToValues(method)) ||
        !isAssessmentMethodReady(method),
    )
  ) {
    return true;
  }

  if (page.telehealth_readiness_assessment_needed && hasAssessmentReadinessErrors(page)) {
    return true;
  }

  if (!ASSESSMENT_SUMMARY_FORM_SCHEMA.isValidSync(mapAssessmentPageToSummaryValues(page))) {
    return true;
  }

  return !ASSESSMENT_OBSERVATIONS_FORM_SCHEMA.isValidSync({
    observations: page.observations ?? '',
  });
}
